<template>
  <div class="container-fluid">
    <div class="loading" v-if="isLoading">Loading&hellip;</div>
    <div class="view-header my-4">
      <div class="view-header__main">
        <div class="view-header__info">
          <h1 class="view-header__title">Presupuestos</h1>
          <p class="view-header__subtitle">Gestioná los presupuestos de la sucursal.</p>
        </div>
        <div class="view-header__actions">
          <router-link :to="nuevoPresupuestoLink" class="btn view-header__btn view-header__btn--primary">
            <i class="bi bi-plus-lg"></i>
            Nuevo presupuesto
          </router-link>
        </div>
      </div>
    </div>
    <!-- FILTROS -->
    <div class="filters-panel">
      <div class="filters-panel__section">
        <div class="filters-panel__section-title">
          <i class="bi bi-search"></i>
          Buscador
        </div>
        <div class="filters-panel__row">
          <div class="filters-panel__field">
            <input v-model="doSearch" type="search" class="form-control" placeholder="Buscar...">
          </div>
          <div class="filters-panel__field filters-panel__field--inline">
            <label class="control-label">Fecha:</label>
            <Daterange ref="daterange" :opendir="'left'" :initial-start="dateRangeStart" :initial-end="dateRangeEnd" @datepicked="onDatePicked" />
          </div>
          <div class="filters-panel__actions">
            <button type="button" class="filters-panel__btn filters-panel__btn--outline" @click="clearFilters">
              <i class="bi bi-eraser"></i>
              Limpiar filtros
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- TABLA -->
    <div class="data-table">
      <vdtnet-table
        ref="table"
        :fields="fields"
        :opts="opts"
        :selectCheckbox="0"
        container-class-name="table-responsive"
        class-name="table mb-0"
        @budgetDetail="getBudgetDetail"
        @convertToOrderNote="convertToOrderNoteMethod"
        @convertToSale="convertToSaleMethod"
        @convertToCombo="convertToComboMethod"
        @getPDF="downloadPDF"
        @delete="deleteBudget"
      />
    </div>
  </div>
</template>

<script>
import VdtnetTable from '@/components/global/VdtnetTable.vue';
import { mapState } from 'vuex';
import Daterange from "../../global/Daterange-component.vue";
import formatDateMixin from "../../../mixins/formatDateMixin";
import formatMoneyMixin from "../../../mixins/formatMoneyMixin";
import myaxios from 'axios';

export default {
  components: {
    VdtnetTable,
    Daterange,
  },
  mixins: [formatDateMixin, formatMoneyMixin],
  data: function () {
    const defaultRange = this.last30DaysRange();
    return {
      doSearch: '',
      isLoading: false,
      dateRangeStart: defaultRange.startDate,
      dateRangeEnd: defaultRange.endDate,
      opts: {
        language: {
          url: '//cdn.datatables.net/plug-ins/1.13.4/i18n/es-ES.json',
        },
        ajax: {
          url: process.env.VUE_APP_BASE_URL + "/api/budgets/list",
          type: "GET",
          data: (d) => {
            d.branchoffice_id = this.branchofficeId;
            d.daterange = this.daterange;
            return d;
          },
          headers: { Authorization: "Bearer " + localStorage.getItem("token") },
        },
        processing: false,
        pageLength: 15,
        searching: true,
        searchDelay: 1500,
        destroy: true,
        ordering: true,
        lengthChange: true,
        serverSide: true,
        fixedHeader: true,
        autoWidth: false,
        order: [[1, "desc"]],
      },
      fields: {
        id: { visible: false },
        number: { label: "N°", width: "70px" },
        created_at: {
          label: "Fecha",
          width: "130px",
          render: (data) => new Date(data).toLocaleDateString('es-AR'),
        },
        client: {
          label: "Cliente",
          render: (data, type) =>
            type === 'display' ? `<span class="data-table__primary">${data}</span>` : data,
        },
        user: { label: "Vendedor", width: "120px" },
        branchoffice: { label: "Sucursal", width: "120px" },
        total: {
          label: "Total",
          width: "110px",
          align: "right",
          render: (data, type) => {
            const formatted = this.formatoDinamicoMoneda(data, type);
            return type === 'display' ? `<span class="data-table__primary">${formatted}</span>` : formatted;
          },
        },
        actions: {
          isLocal: true,
          label: "Acciones",
          width: "180px",
          align: "right",
          defaultContent: `
            <div class="table-action-group">
              <button data-action="budgetDetail" class="table-action-btn" type="button" title="Ver detalle">
                <i class="bi bi-file-earmark-text"></i>
              </button>
              <button data-action="getPDF" class="table-action-btn" type="button" title="Descargar PDF">
                <i class="bi bi-filetype-pdf"></i>
              </button>
              <button data-action="convertToOrderNote" class="table-action-btn" type="button" title="Convertir en nota de pedido">
                <i class="bi bi-box-seam"></i>
              </button>
              <button data-action="convertToSale" class="table-action-btn" type="button" title="Convertir en venta">
                <i class="bi bi-basket"></i>
              </button>
              <button data-action="convertToCombo" class="table-action-btn" type="button" title="Convertir en combo">
                <i class="bi bi-boxes"></i>
              </button>
              <button data-action="delete" class="table-action-btn" type="button" title="Eliminar comprobante">
                <i class="bi bi-trash"></i>
              </button>
            </div>
          `,
        },
      },
    };
  },
  mounted() {
    this.$nextTick(() => {
      const dt = this.$refs.table && this.$refs.table.dataTable;
      if (!dt) return;
      dt.on('preXhr.dt', () => { this.isLoading = true; });
      dt.on('xhr.dt', () => { this.isLoading = false; });
    });
  },
  watch: {
    doSearch(newValue, oldValue) {
      if (newValue.length >= 4 || oldValue.length >= 4) {
        this.$refs.table.search(this.doSearch);
      }
      if (newValue === '' && oldValue.length > 0) {
        this.$refs.table.search(this.doSearch);
      }
    },
    branchofficeId() {
      this.refreshTable();
    },
    daterange() {
      this.refreshTable();
    },
  },
  methods: {
    refreshTable() {
      if (this.$refs.table !== undefined) this.$refs.table.reload();
    },
    onDatePicked(date) {
      this.dateRangeStart = date.startDate;
      this.dateRangeEnd = date.endDate;
    },
    clearFilters() {
      this.doSearch = '';
      const defaultRange = this.last30DaysRange();
      this.dateRangeStart = defaultRange.startDate;
      this.dateRangeEnd = defaultRange.endDate;
    },
    getBudgetDetail(data) {
      this.$router.push({ name: 'Detalle de presupuesto', params: { budgetId: data.id } });
    },
    convertToOrderNoteMethod(data) {
      this.$router.push({ name: 'Nueva ON desde presupuesto', params: { budgetId: data.id } });
    },
    convertToSaleMethod(data) {
      this.$router.push({ name: 'Nueva venta desde presupuesto', params: { budgetId: data.id } });
    },
    convertToComboMethod(data) {
      this.$router.push({ name: 'Nuevo combo desde presupuesto', params: { budgetId: data.id } });
    },
    downloadPDF(data) {
      const filename = data.id + '.pdf';
      this.$http.get("/api/budgets/budPDF/" + data.id).then((res) => {
        const uninterceptedAxiosInstance = myaxios.create();
        uninterceptedAxiosInstance.get(res.data, { responseType: 'blob' }).then((blobRes) => {
          const blob = new Blob([blobRes.data], { type: 'application/pdf' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = filename;
          link.click();
          URL.revokeObjectURL(link.href);
        });
      });
    },
    deleteBudget(data) {
      if (!confirm("¿Está seguro que desea borrar este comprobante?")) return;
      this.$http.post("/api/budgets/delete", { id: data.id }).then((response) => {
        if (response.data.code === 201) this.refreshTable();
      });
    },
  },
  computed: {
    daterange() {
      if (!this.dateRangeStart || !this.dateRangeEnd) return null;
      return `${this.formatoFecha2Daterange(this.dateRangeStart)} - ${this.formatoFecha2Daterange(this.dateRangeEnd)}`;
    },
    nuevoPresupuestoLink() {
      return { name: 'Nuevo presupuesto' };
    },
    ...mapState('branchoffices', {
      branchofficeId: state => state.selectedBranchofficeId,
    }),
  },
};
</script>

<style scoped>
.filters-panel__field--inline {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filters-panel__field--inline .control-label {
  margin: 0;
  white-space: nowrap;
  font-size: 13px;
  color: var(--text);
}

.filters-panel__field--inline > .form-group {
  flex: 1 1 auto;
  margin-bottom: 0;
}
</style>
